/**
 * Strategy for the three times a stationary proposal suggests up front.
 *
 * Two goals pull against each other: every customer must see a stable set (the
 * times are not held, so a reload that reshuffles them looks broken), and the
 * customers as a group must spread across the calendar rather than pile onto the
 * first free morning. A seeded stream gives both — same proposal, same picks;
 * different proposals, different picks.
 */

/**
 * Open days of lead before the first suggestion, counting today as day 1.
 * Tuesday + 4 open days lands on Friday.
 */
const SUGGESTED_TIMES_LEAD_OPEN_DAYS = 4;

/** Open days of capacity searched, counting the lead day as day 1. */
const SUGGESTED_TIMES_WINDOW_OPEN_DAYS = 4;

/** Upper bound only — fewer distinct times than this are shown as-is, never padded. */
export const SUGGESTED_TIMES_COUNT = 3;

/** Hours to confirm before the suggestion is treated as stale. */
const SUGGESTED_TIMES_CONFIRM_WINDOW_HOURS = 24;

/**
 * When the customer must confirm by. The suggestions are not held, so this is a
 * freshness promise rather than a reservation TTL — after it the set is re-picked
 * against whatever capacity is left.
 */
export const getSuggestedTimesConfirmDeadline = (from: Date | number = Date.now()): Date =>
  new Date(new Date(from).getTime() + SUGGESTED_TIMES_CONFIRM_WINDOW_HOURS * 60 * 60 * 1000);

const SUNDAY = 0;

/** Garages are closed Sundays, so a Sunday is never a suggestion and never consumes a counted day. */
export const isOpenDay = (date: Date) => date.getDay() !== SUNDAY;

/**
 * The `n`th open day at or after `from`, counting `from` itself as day 1 when it
 * is open. Sundays are stepped over without consuming a count.
 */
const nthOpenDay = (from: Date, n: number): Date => {
  const cursor = new Date(from);
  const seen = { count: isOpenDay(cursor) ? 1 : 0 };

  while (seen.count < n) {
    cursor.setDate(cursor.getDate() + 1);
    if (isOpenDay(cursor)) {
      seen.count += 1;
    }
  }
  return cursor;
};

const isSameCalendarDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

/**
 * The open day a proposal's suggested date should lead with.
 * A date before `from` is ignored. A Sunday moves to Monday — garages are closed.
 */
export const getSuggestedDateAnchor = (
  dateSuggested: string | null | undefined,
  from: Date | number = Date.now()
): Date | null => {
  if (!dateSuggested) {
    return null;
  }

  const anchor = new Date(`${dateSuggested.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(anchor.getTime())) {
    return null;
  }

  const fromDate = new Date(from);
  const usable = isSameCalendarDay(anchor, fromDate) || anchor.getTime() > fromDate.getTime();
  if (!usable) {
    return null;
  }

  return nthOpenDay(anchor, 1);
};

/**
 * Inclusive date range the suggestions are drawn from: {@link SUGGESTED_TIMES_LEAD_OPEN_DAYS}
 * open days out, then {@link SUGGESTED_TIMES_WINDOW_OPEN_DAYS} open days wide.
 * From a Tuesday that is Friday through the following Tuesday, Sunday skipped.
 * A usable suggested date replaces the lead, so that day is the first one in the window.
 */
export const getSuggestionWindow = (from: Date | number = Date.now(), dateSuggested?: string | null) => {
  const anchor = getSuggestedDateAnchor(dateSuggested, from);
  const start = anchor ?? nthOpenDay(new Date(from), SUGGESTED_TIMES_LEAD_OPEN_DAYS);
  return { start, end: nthOpenDay(start, SUGGESTED_TIMES_WINDOW_OPEN_DAYS), anchored: anchor != null };
};

type DatedSlot = {
  id: string;
  /** Calendar day in the garage's timezone — the unit suggestions spread over. */
  dayKey: string;
};

type PickOptions = {
  /** Stable per proposal (the proposal id) so a reload returns the same times. */
  seed: number;
  count?: number;
  /**
   * When this day has capacity, one of its times is always included.
   * The caller passes the suggested date so it leads the list.
   */
  pinDayKey?: string;
};

/**
 * mulberry32 — 32-bit PRNG with a full period and no external dependency.
 * Held in an object rather than a `let` so the module stays free of mutable bindings.
 */
const createSeededRandom = (seed: number) => {
  const state = { value: seed >>> 0 };

  return () => {
    state.value = (state.value + 0x6d2b79f5) >>> 0;
    const mixed = Math.imul(state.value ^ (state.value >>> 15), state.value | 1);
    const folded = mixed ^ (mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61));
    return ((folded ^ (folded >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Shuffle by sorting on a drawn key. Keys are drawn in array order, so the
 * result is fully determined by the seed and the input order.
 */
const shuffleWith = <T>(items: T[], random: () => number): T[] =>
  items
    .map((item) => ({ item, key: random() }))
    .sort((a, b) => a.key - b.key)
    .map(({ item }) => item);

const groupByDay = <T extends DatedSlot>(slots: T[]): T[][] => {
  const byDay = new Map<string, T[]>();
  for (const slot of slots) {
    const existing = byDay.get(slot.dayKey);
    if (existing) {
      existing.push(slot);
    } else {
      byDay.set(slot.dayKey, [slot]);
    }
  }
  return [...byDay.values()];
};

/**
 * Pick up to `count` slots, at most one per day while distinct days last, then
 * backfill from the days already used. Input order decides the chronological
 * order of the result; the seed decides which day and which time within it.
 */
const pickSpread = <T extends DatedSlot>(slots: T[], seed: number, count: number): T[] => {
  if (slots.length === 0 || count <= 0) {
    return [];
  }

  const random = createSeededRandom(seed);
  const days = shuffleWith(groupByDay(slots), random);

  // One per day first — three times on the same morning is not a real choice.
  const oneEach = days.slice(0, count).map((daySlots) => shuffleWith(daySlots, random)[0]!);

  const remaining = count - oneEach.length;
  const backfill =
    remaining > 0
      ? shuffleWith(
          slots.filter((slot) => !oneEach.includes(slot)),
          random
        ).slice(0, remaining)
      : [];

  const picked = new Set([...oneEach, ...backfill]);
  return slots.filter((slot) => picked.has(slot));
};

export const pickSuggestedSlots = <T extends DatedSlot>(
  slots: T[],
  { seed, count = SUGGESTED_TIMES_COUNT, pinDayKey }: PickOptions
): T[] => {
  if (slots.length === 0 || count <= 0) {
    return [];
  }

  const pinDaySlots = pinDayKey ? slots.filter((slot) => slot.dayKey === pinDayKey) : [];
  if (pinDaySlots.length === 0) {
    return pickSpread(slots, seed, count);
  }

  const random = createSeededRandom(seed);
  const pinnedSlot = shuffleWith(pinDaySlots, random)[0]!;
  const others = pickSpread(
    slots.filter((slot) => slot.dayKey !== pinDayKey),
    seed,
    count - 1
  );
  const stillNeeded = count - 1 - others.length;
  const pinBackfill =
    stillNeeded > 0
      ? shuffleWith(
          pinDaySlots.filter((slot) => slot !== pinnedSlot),
          random
        ).slice(0, stillNeeded)
      : [];

  const picked = new Set([pinnedSlot, ...others, ...pinBackfill]);
  return slots.filter((slot) => picked.has(slot));
};
